import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, lessonLabel, type TeacherPlanInfo } from "./api";
import { Close } from "./icons";
import "./grading.css";
import "./LessonPlan.css";

// Teacher lesson plans (Paul, Oct 10): a one-screen summary of a lesson's goal, meeting flow, what
// success looks like and what to watch for. Teachers only: the button renders only for teachers, and
// the plan comes from a teacher-only route (GET /api/assignments/:id/teacher-plan), so it never
// ships to a student's browser. Opened from the lesson cards on Home and from Today's mission.

const MODE_NAMES: Record<string, string> = { plan: "Plan", build: "Build", ask: "Ask" };

/** A small "Lesson plan" button that opens the plan dialog. Render it for teachers only. */
export function LessonPlanButton({ assignmentId, lesson, className = "" }: { assignmentId: string; lesson: number; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`outline small plan-btn ${className}`} aria-label={`Lesson plan for ${lessonLabel(lesson)}`} title="Teacher lesson plan" onClick={() => setOpen(true)}>
        Lesson plan
      </button>
      {/* On document.body, so it isn't inside a card (and prints from the top of the page). */}
      {open && createPortal(<LessonPlanDialog assignmentId={assignmentId} onClose={() => setOpen(false)} />, document.body)}
    </>
  );
}

function List({ items }: { items: string[] }) {
  return (
    <ul>
      {items.map((t, i) => (
        <li key={i}>{t}</li>
      ))}
    </ul>
  );
}

export function LessonPlanDialog({ assignmentId, onClose }: { assignmentId: string; onClose: () => void }) {
  const [plan, setPlan] = useState<TeacherPlanInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const close = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    api.teacherPlan(assignmentId).then(
      (r) => setPlan(r.plan),
      (e: Error) => setError(e.message),
    );
    close.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [assignmentId]);

  return (
    <div className="dlg-backdrop plan-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dlg plan-dlg" role="dialog" aria-modal="true" aria-labelledby="plan-title">
        <div className="plan-top">
          <div className="dlg-head">
            <span className="eyebrow">Teacher lesson plan{plan ? ` · ${lessonLabel(plan.lesson)}` : ""}</span>
            <h2 id="plan-title">{plan ? plan.title : "Lesson plan"}</h2>
            {plan && <p className="plan-when">{plan.when}</p>}
          </div>
          <div className="plan-actions no-print">
            {plan && (
              <button type="button" className="outline small" onClick={() => window.print()}>
                Print
              </button>
            )}
            <button type="button" ref={close} className="icon-close" aria-label="Close" onClick={onClose}>
              <Close size={16} />
            </button>
          </div>
        </div>

        {!plan && !error && <p className="muted">Loading the plan…</p>}
        {error && <p className="dlg-error">{error}</p>}

        {plan && (
          <div className="plan-body">
            <section className="plan-goal" aria-label="Big idea">
              <span className="plan-label">Big idea · {plan.pattern}</span>
              <p className="plan-idea">{plan.bigIdea}</p>
              <p className="small muted">
                Skill: {plan.skill} · Modes: {plan.modes.map((m) => MODE_NAMES[m] ?? m).join(", ")}
              </p>
            </section>

            <section aria-label="What students make">
              <h3>What students make</h3>
              <p>{plan.make}</p>
            </section>

            <section aria-label="Meeting flow">
              <h3>Meeting flow</h3>
              {plan.meetings.map((m) => (
                <div key={m.title} className="plan-meeting">
                  {plan.meetings.length > 1 && <h4>{m.title}</h4>}
                  <ol className="plan-flow">
                    {m.flow.map((s, i) => (
                      <li key={i}>
                        <span className="plan-min">{s.minutes} min</span>
                        <span>
                          <b>{s.segment}.</b> {s.what}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </section>

            <div className="plan-cols">
              <section aria-label="What success looks like">
                <h3>What success looks like</h3>
                <List items={plan.success} />
                <p className="plan-sub">The app checks at Submit:</p>
                <ul className="plan-checks">
                  {plan.evidence.map((e, i) => (
                    <li key={i}>
                      {e.label}
                      {e.bonus && <span className="plan-bonus">bonus</span>}
                    </li>
                  ))}
                </ul>
              </section>
              <section aria-label="Watch for">
                <h3>Watch for</h3>
                <List items={plan.watchFor} />
              </section>
              <section aria-label="Setup">
                <h3>Before class</h3>
                <List items={plan.setup} />
                {plan.explainers.length > 0 && <p className="small muted">Explainers: {plan.explainers.map((x) => x.title).join(" · ")}</p>}
              </section>
              <section aria-label="Questions">
                <h3>Discussion</h3>
                <List items={plan.discuss} />
                <p className="plan-sub">Exit ticket (Submit box):</p>
                <List items={plan.reflection} />
                {plan.stretch && (
                  <p className="small">
                    <b>Stretch:</b> {plan.stretch}
                  </p>
                )}
              </section>
            </div>

            <details className="plan-mission no-print">
              <summary>What students see (Today's mission)</summary>
              <p>{plan.instructions}</p>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
